import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select
} from '@material-ui/core';
import { useState } from 'react';
import { CustomInputLabel } from '../../../../../../../../components/CustomInputLabel';
import { DialogTitleWithTypography } from '../../../../../../../../components/DialogTitleWithTypography';
import {
  ItemSorter,
  RowsRenderer
} from '../../../../../../../../components/GroupableList';
import { IColumn } from '../../../../../../../../components/Table/VirtualizedSortable';
import { FlexContainerVertical } from '../../../../../../../../layout/Flex';
import { updateDoc } from '../../../../../../../../services/firebase';
import { OrgSelector } from '../../../../../../../components/OrgSelector';

type OtherProps = {
  fieldsToAdd: CustomUserField[];
  setFieldsToAdd: (nextFieldsToAdd: CustomUserField[]) => void;
};

type Column = IColumn<CustomUserField, 'label' | 'key' | 'type', OtherProps>;

const COLUMNS: Column[] = [
  {
    key: 'label',
    head: () => 'Name',
    cell: (d) => d.label,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'key',
    head: () => 'Key',
    cell: (d) => d.key,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 1
  },
  {
    key: 'type',
    head: () => 'Type',
    cell: (d) => d.type,
    align: 'left',
    sortable: false,
    defaultDirection: 'asc',
    width: 120,
    flexGrow: 1
  }
];

const Grid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(6)
}));

const SORTER: ItemSorter<CustomUserField, OtherProps> = {
  key: 'label',
  items: { sort: (d) => d.label, dir: 'asc' }
};

export const ImportCustomDataFieldsDialog = ({
  open,
  onClose,
  targetOrg
}: {
  open: boolean;
  onClose: () => void;
  targetOrg: Doc<IOrganization>;
}) => {
  const [fieldsToAdd, setFieldsToAdd] = useState<CustomUserField[]>([]);
  const [sourceOrg, setSourceOrg] = useState<Doc<IOrganization> | null>(null);
  const otherProps: OtherProps = {
    fieldsToAdd,
    setFieldsToAdd
  };
  const isFieldSelected = (f: CustomUserField) =>
    fieldsToAdd.find((of) => of.key === f.key);
  const theme = useTheme();
  return (
    <Dialog open={open} onClose={onClose} fullScreen fullWidth>
      <DialogTitleWithTypography variant="h3">
        Import Custom Data Fields from other org
      </DialogTitleWithTypography>
      <DialogContent>
        <Grid>
          <div>
            <FlexContainerVertical fullWidth spacing={3}>
              <CustomInputLabel label="Target Org" fullWidth>
                <Select readOnly value={targetOrg.id} fullWidth disabled>
                  <MenuItem value={targetOrg.id}>
                    {targetOrg.data.name}
                  </MenuItem>
                </Select>
              </CustomInputLabel>
              <RowsRenderer
                renderHead
                fullWidth
                variant="plain"
                rows={[...targetOrg.data.customUserFields, ...fieldsToAdd]}
                rowToKey={(f) => f.key}
                columns={COLUMNS}
                sorter={SORTER}
                sortDirection={SORTER.items.dir}
                otherProps={otherProps}
                rowToClassName={(f) =>
                  isFieldSelected(f)
                    ? css({ background: theme.custom.colors.green.w200 })
                    : css({ cursor: 'default' })
                }
                onRowClick={(f) => {
                  if (!isFieldSelected(f)) {
                    return;
                  }
                  setFieldsToAdd((x) => x.filter((of) => of.key !== f.key));
                }}
              />
            </FlexContainerVertical>
          </div>
          <div>
            <FlexContainerVertical fullWidth spacing={3}>
              <CustomInputLabel label="Source Org" fullWidth>
                <OrgSelector value={sourceOrg} onChange={setSourceOrg} />
              </CustomInputLabel>

              <RowsRenderer
                renderHead
                fullWidth
                variant="plain"
                rows={sourceOrg?.data.customUserFields || EMPTY_ARR}
                rowToKey={(f) => f.key}
                columns={COLUMNS}
                sorter={SORTER}
                sortDirection={SORTER.items.dir}
                otherProps={otherProps}
                rowToClassName={(f) =>
                  targetOrg.data.customUserFields.find((of) => of.key === f.key)
                    ? css({
                        background: theme.custom.colors.gray.w200,
                        cursor: 'default'
                      })
                    : isFieldSelected(f)
                    ? css({ background: theme.custom.colors.green.w200 })
                    : ''
                }
                onRowClick={(f) => {
                  if (
                    targetOrg.data.customUserFields.find(
                      (of) => of.key === f.key
                    )
                  ) {
                    return;
                  }
                  if (isFieldSelected(f)) {
                    setFieldsToAdd((x) => x.filter((of) => of.key !== f.key));
                  } else {
                    setFieldsToAdd((x) => [...x, f]);
                  }
                }}
              />
            </FlexContainerVertical>
          </div>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!fieldsToAdd.length}
          onClick={async () => {
            setFieldsToAdd([]);
            await updateDoc(targetOrg, (x) => ({
              customUserFields: [...x.customUserFields, ...fieldsToAdd]
            }));
          }}
        >
          Import {fieldsToAdd.length} new field(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
